import {repositoryPath} from '@github-ui/paths'
import {queryOptions} from '@github-ui/react-query'
import {ReactionViewerRelayServerQuery} from '@github-ui/server-persisted-queries'
import type {ReactionViewerRelayServerQueryQuery} from '@github-ui/server-persisted-queries/types/ReactionViewerRelayServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {ReactionViewerGroupsFragment} from './reaction-viewer-types'

export type ReactionViewerQueryData = {
  node: ({id: string} & ReactionViewerGroupsFragment) | null
}

export const reactionViewerQueryKeyPrefix = (owner: string, repo: string) => ['reaction-viewer', owner, repo] as const

export const reactionViewerQueryKey = (owner: string, repo: string, subjectId: string) =>
  [...reactionViewerQueryKeyPrefix(owner, repo), subjectId] as const

async function fetchReactionViewer({
  owner,
  repo,
  subjectId,
  signal,
}: {
  owner: string
  repo: string
  subjectId: string
  signal?: AbortSignal
}): Promise<ReactionViewerQueryData> {
  const params = new URLSearchParams({queryId: ReactionViewerRelayServerQuery})
  const basePath = repositoryPath({owner, repo})
  const response = await reactFetchJSON(`${basePath}/reaction_viewer/${encodeURIComponent(subjectId)}?${params}`, {
    signal,
  })

  if (!response.ok) throw new Error(`Reaction viewer fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: ReactionViewerRelayServerQueryQuery; errors?: unknown[]}
  if (!json.data) throw new Error('Reaction viewer query returned no data')

  const node = json.data.node
  return {node: node && 'reactionGroups' in node ? node : null}
}

export function reactionViewerQueryOptions({
  owner,
  repo,
  subjectId,
  initialData,
}: {
  owner: string
  repo: string
  subjectId: string
  initialData?: ReactionViewerGroupsFragment | null
}) {
  return queryOptions({
    queryKey: reactionViewerQueryKey(owner, repo, subjectId),
    queryFn: ({signal}) => fetchReactionViewer({owner, repo, subjectId, signal}),
    initialData: initialData ? {node: {id: subjectId, ...initialData}} : undefined,
    staleTime: Infinity,
    throwOnError: (_error, query) => query.state.data === undefined,
  })
}
